import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from 'prop-types';
import { LineChart, Line, XAxis, YAxis, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, CartesianGrid, Surface, Symbols } from "recharts";
import { formatValue, formatPercentage, formatSeriesName } from "./Utils.js";
import _ from "underscore";
import moment from "moment";
import { fillColors } from "../constants.js";
import { useFilterStoreWithDayjsDates } from "../store/filterStore.js";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import "./DropdownMenu.css";
import SmartRechartsTooltip from "./chart-components/SmartRechartsTooltip.jsx";


//return the groupings in the order of the measure, including "Other" if the number of groupings is more than the max groupings when specified
function getGroupings(data, maxGroupings, includeOthers = false, includeForecast = false) {
	var groupings = [];
	var groupedData = _.groupBy(data, "grouping");
	for (const group in groupedData) {
		let groupSum = groupedData[group].reduce((acc, curr) => acc + curr.measure, 0);

		groupings.push({
			grouping: group,
			measure: groupSum,
		});

		if (includeForecast) {
			let groupForecast = groupedData[group].reduce((acc, curr) => acc + (curr.forecast || 0), 0);
			groupings.push({
				grouping: group + " forecast",
				measure: groupForecast,
			});
		}
	}

	// Create a sorted copy to avoid mutating the original array
	groupings = [...groupings].sort((a, b) => b.measure - a.measure);

	let returnValue = groupings.map(item => item.grouping).slice(0, maxGroupings);

	// if the number of groupings is more than the max groupings and other should be included, add "Other" value
	if (includeOthers && returnValue.length >= maxGroupings) {
		returnValue.push("Other");
	}

	return returnValue;
}

function keepOnlyTopGroupings(data, groupings) {
	var result = [];

	if (data && data.length > 0) {
		data.forEach(item => {
			if (groupings.find(group => group === item.grouping)) {
				result.push(item);
			} else {
				result.push({
					...item,
					grouping: "Other"
				})
			}
		});
	}

	return result;
}

function prepareData(data, maxGroupings, dateFormat, includeForecast = false) {

	// check whether the grouping has more than 10 values and order the grouping accordingly
	const groupings = getGroupings(data, maxGroupings, false, includeForecast);	
	const preparedData = keepOnlyTopGroupings(data, groupings);

	// First pass: determine transition points for each grouping
	// Find the last point with actual data and first point with forecast data per grouping
	const transitionPoints = {};
	if (includeForecast) {
		const groupedByGrouping = _.groupBy(preparedData, "grouping");
		for (const [grouping, items] of Object.entries(groupedByGrouping)) {
			// Sort by time to find transition - create a copy to avoid mutating the original array
			const sortedItems = [...items].sort((a, b) => moment(a.time).valueOf() - moment(b.time).valueOf());
			
			// Find last point with actual data (measure > 0)
			let lastActualIndex = -1;
			let lastActualValue = 0;
			for (let i = sortedItems.length - 1; i >= 0; i--) {
				if (sortedItems[i].measure && sortedItems[i].measure > 0) {
					lastActualIndex = i;
					lastActualValue = sortedItems[i].measure;
					break;
				}
			}
			
			// Find first point with forecast data (forecast > 0)
			let firstForecastIndex = sortedItems.length;
			for (let i = 0; i < sortedItems.length; i++) {
				if (sortedItems[i].forecast && sortedItems[i].forecast > 0) {
					firstForecastIndex = i;
					break;
				}
			}
			
			transitionPoints[grouping] = {
				lastActualTime: lastActualIndex >= 0 ? sortedItems[lastActualIndex].time : null,
				lastActualValue: lastActualValue,
				firstForecastTime: firstForecastIndex < sortedItems.length ? sortedItems[firstForecastIndex].time : null
			};
		}
	}

	var d = _.groupBy(preparedData, "time");

	var result = [];

	for (const [key, value] of Object.entries(d)) {
		var prevData = null;
		var o = {};
		var total = 0;
		const currentTime = key;
		const itemTime = moment(currentTime);

		// First, aggregate all values by grouping for this time point
		const groupingSums = {};
		for (const item of value) {
			if (!groupingSums[item.grouping]) {
				groupingSums[item.grouping] = { actual: 0, forecast: 0 };
			}
			groupingSums[item.grouping].actual += item.measure || 0;
			if (includeForecast) {
				groupingSums[item.grouping].forecast += item.forecast || 0;
			}
		}

		// Then, apply transition logic and set values in o
		for (const [grouping, sums] of Object.entries(groupingSums)) {
			let actualValue = sums.actual;
			let forecastValue = sums.forecast;
			
			if (includeForecast && transitionPoints[grouping]) {
				const transition = transitionPoints[grouping];
				const lastActualTime = transition.lastActualTime ? moment(transition.lastActualTime) : null;
				const firstForecastTime = transition.firstForecastTime ? moment(transition.firstForecastTime) : null;
				
				// If we have a transition point, enforce separation but allow connection
				if (lastActualTime && firstForecastTime) {
					// Actual line: show up to and including the last actual time
					if (itemTime.isAfter(lastActualTime, 'day')) {
						actualValue = null;
					}
					// Forecast line: show from the last actual time onwards (to ensure connection)
					// Use actual value at transition point if forecast doesn't exist there
					if (itemTime.isBefore(lastActualTime, 'day')) {
						forecastValue = null;
					} else if (itemTime.isSame(lastActualTime, 'day')) {
						// At transition point, use forecast value if available, otherwise use actual value to connect
						if (!forecastValue || forecastValue === 0) {
							forecastValue = actualValue;
						}
					}
				} else if (lastActualTime) {
					// Only actual data exists, no forecast
					if (itemTime.isAfter(lastActualTime, 'day')) {
						actualValue = null;
					}
					forecastValue = null;
				} else if (firstForecastTime) {
					// Only forecast data exists, no actual
					if (itemTime.isBefore(firstForecastTime, 'day')) {
						forecastValue = null;
					}
					actualValue = null;
				}
			}

			// Set the values (use null to break the line, 0 is a valid value)
			o[grouping] = actualValue;
			
			if (includeForecast) {
				o[grouping + " forecast"] = forecastValue;
			}
			
			total += (actualValue !== null ? actualValue : 0) + (includeForecast && forecastValue !== null ? forecastValue : 0);
		}

		var formattedDate = moment(key).format(dateFormat);

		if (result.length > 0) {
			prevData = Object.fromEntries(
				Object.entries(result[result.length - 1])
					.filter(([key]) => !key.endsWith('_prev'))
			);
		}

		result.push({
			_name: formattedDate,
			_original: key,
			_total: total,
			...o,
			...(prevData ? Object.fromEntries(
				Object.entries(prevData).map(([key, value]) => [`${key}_prev`, value])
			) : {}),
		});
	}

	return result;
}

// Helper function to determine value format based on forecast type
const getValueFormat = (forecastType) => {
	return forecastType === "utilization" ? "percent" : "currency";
};

export const ForecastChartComponent = (props) => {
	const { data, title, subtitle, selectedSeries, dateFormat = "MMM D", legendPosition = "bottom",       addFilter,  currentGrouping, useOwnBackground = true, includeForecast = false, showTotals = true, showPreviousMonth = true, showNextMonth = true, forecastType } = props;
	const [hoveredArea, setHoveredArea] = useState(null);
	const [lastHoveredArea, setLastHoveredArea] = useState(null);
	const [menuOpen, setMenuOpen] = useState(false);
	const [menuPos, setMenuPos] = useState({ x: 0, y: 0 });
	const [, setIsRightClickContext] = useState(false);
	const leaveTimeoutRef = React.useRef(null);
	const hoveredAreaRef = React.useRef(null);
	const chartContainerRef = React.useRef(null);
	// Nothing is read from this any more, but the call is kept so the subscription
	// and the hook order stay exactly as they were.
	useFilterStoreWithDayjsDates();
	const pdataRef = React.useRef(null);

	const handleRightClick = (event) => {
		// This is a fallback for right-clicks on empty areas of the chart
		// Individual areas handle their own right-clicks
		if (event && typeof event.preventDefault === 'function') {
			event.preventDefault();
		}
		// Only show menu if we have a hovered area
		if (hoveredArea || lastHoveredArea) {
			// Toggle menu: close if open, open if closed
			if (menuOpen) {
				setMenuOpen(false);
				setIsRightClickContext(false);
			} else {
				// Use clientX/clientY as fallback if pageX/pageY are not available
				const x = event.pageX || event.clientX || 0;
				const y = event.pageY || event.clientY || 0;
				setMenuPos({ x, y });
				setMenuOpen(true);
				setIsRightClickContext(true);
			}
		}
	};

	const handleMenuOpenChange = (open) => {
		// This is called by Radix UI when the menu state changes
		setMenuOpen(open);
		if (!open) {
			setIsRightClickContext(false);
		}
	};

	const handleClose = () => {
		setMenuOpen(false);
		setIsRightClickContext(false);
	};

	const handleClickOutside = (event) => {
		// Only close if clicking outside the menu, not on right-click release
		if (menuOpen && event.button !== 2) { // button 2 is right mouse button
			setMenuOpen(false);
		}
	};

	// Handle global click events to close menu when clicking outside
	useEffect(() => {
		const handleGlobalClick = (event) => {
			// Only close on left click outside the menu
			if (menuOpen && event.button === 0) { // button 0 is left mouse button
				const menuElement = document.querySelector('[data-radix-popper-content-wrapper]');
				if (menuElement && !menuElement.contains(event.target)) {
					setMenuOpen(false);
					setIsRightClickContext(false);
				}
			}
		};

		if (menuOpen) {
			document.addEventListener('mousedown', handleGlobalClick);
			return () => document.removeEventListener('mousedown', handleGlobalClick);
		}
	}, [menuOpen]);

	const maxGroupings = 10;
	const pdata = prepareData(data, maxGroupings, dateFormat, includeForecast);
	
	// Shared date boundaries for current and previous month calculations
	const monthBoundaries = useMemo(() => {
		const now = moment();
		const currentMonthStart = now.clone().startOf('month');
		const currentMonthEnd = now.clone().endOf('month');
		const previousMonthStart = currentMonthStart.clone().subtract(1, 'month').startOf('month');
		const previousMonthEnd = currentMonthStart.clone().subtract(1, 'day').endOf('day');
		const nextMonthStart = currentMonthEnd.clone().add(1, 'day').startOf('day');
		const nextMonthEnd = currentMonthEnd.clone().add(1, 'month').endOf('month');
		
		return {
			currentMonthStart,
			currentMonthEnd,
			previousMonthStart,
			previousMonthEnd,
			nextMonthStart,
			nextMonthEnd
		};
	}, []);
	
	// Filter chart data based on showPreviousMonth and showNextMonth options
	// This only affects the chart display, not the totals calculations
	const filteredPdata = useMemo(() => {
		if (!pdata || pdata.length === 0) return pdata;
		
		const { previousMonthStart, previousMonthEnd, nextMonthStart, nextMonthEnd } = monthBoundaries;
		
		return pdata.filter(item => {
			if (!item._original) return true;
			const itemDate = moment(item._original);
			
			// Check if item is in previous month
			if (itemDate.isBetween(previousMonthStart, previousMonthEnd, 'day', '[]')) {
				return showPreviousMonth;
			}
			
			// Check if item is in next month
			if (itemDate.isBetween(nextMonthStart, nextMonthEnd, 'day', '[]')) {
				return showNextMonth;
			}
			
			// Always show current month
			return true;
		});
	}, [pdata, showPreviousMonth, showNextMonth, monthBoundaries]);
	
	const sortedHeaders = getGroupings(data, maxGroupings, true, includeForecast);
	const maxValue = filteredPdata && filteredPdata.length > 0 
		? Math.max(...filteredPdata.map(item => item._total)) 
		: 0;

	// Calculate total spent of the previous month (or average utilization for utilization forecasts)
	const previousMonthTotal = useMemo(() => {
		const { previousMonthStart, previousMonthEnd } = monthBoundaries;
		
		if (forecastType === "utilization") {
			// For utilization, calculate average utilization for the previous month
			if (!data || data.length === 0) return 0;
			
			// Filter data points that fall within the previous month (actual data only)
			const previousMonthData = data.filter(item => {
				if (!item.time) return false;
				const itemDate = moment(item.time);
				return itemDate.isBetween(previousMonthStart, previousMonthEnd, 'day', '[]') &&
					   item.measure !== undefined && item.measure !== null && item.measure !== 0;
			});
			
			if (previousMonthData.length === 0) return 0;
			
			// Calculate average utilization
			const sum = previousMonthData.reduce((sum, item) => sum + (item.measure || 0), 0);
			return sum / previousMonthData.length;
		}
		
		// For cost, calculate previous month total
		if (!pdata || pdata.length === 0) return 0;
		
		// Filter data points that fall within the previous month
		const previousMonthData = pdata.filter(item => {
			if (!item._original) return false;
			const itemDate = moment(item._original);
			return itemDate.isBetween(previousMonthStart, previousMonthEnd, 'day', '[]');
		});
		
		// Sum up the totals for the previous month
		return previousMonthData.reduce((sum, item) => sum + (item._total || 0), 0);
	}, [pdata, data, forecastType, monthBoundaries]);

	// Calculate forecasted monthly spent for the current month (or expected average utilization for utilization forecasts)
	// Sums actual costs for days that have passed and adds forecast for remaining days
	// For utilization, calculates expected average utilization for the current month
	const forecastedCurrentMonthTotal = useMemo(() => {
		const { currentMonthStart, currentMonthEnd } = monthBoundaries;
		
		if (forecastType === "utilization") {
			// For utilization, calculate expected average utilization for the current month (forecast data)
			if (!data || data.length === 0) return 0;
			
			// Filter data points that fall within the current month (forecast data only)
			const currentMonthData = data.filter(item => {
				if (!item.time) return false;
				const itemDate = moment(item.time);
				return itemDate.isBetween(currentMonthStart, currentMonthEnd, 'day', '[]') &&
					   item.forecast !== undefined && item.forecast !== null && item.forecast !== 0;
			});
			
			if (currentMonthData.length === 0) return 0;
			
			// Calculate average utilization from forecast data
			const sum = currentMonthData.reduce((sum, item) => sum + (item.forecast || 0), 0);
			return sum / currentMonthData.length;
		}
		
		// For cost, calculate current month forecasted total
		if (!data || data.length === 0) return 0;
		
		// Filter data points that fall within the current month
		const currentMonthData = data.filter(item => {
			if (!item.time) return false;
			const itemDate = moment(item.time);
			return itemDate.isBetween(currentMonthStart, currentMonthEnd, 'day', '[]');
		});
		
		// Sum actual costs for past/today and forecasts for future days
		return currentMonthData.reduce((sum, item) => {
			if (!item.time) return sum;
			
			// Use measure if it exists, otherwise use forecast
			const value = (item.measure !== undefined && item.measure !== null && item.measure !== 0)
				? item.measure
				: (item.forecast || 0);

			return sum + value;
		}, 0);
	}, [data, forecastType, monthBoundaries]);

	// Get month names
	const previousMonthName = useMemo(() => {
		return moment().subtract(1, 'month').format('MMMM YYYY');
	}, []);

	const currentMonthName = useMemo(() => {
		return moment().format('MMMM YYYY');
	}, []);

	// Keep pdata and transitionPoints in refs for callbacks defined before they're initialized
	const transitionPointsRef = React.useRef(null);
	useEffect(() => {
		pdataRef.current = pdata;
		// Store transition points in ref for use in activeDot
		// Compute transition points the same way as in prepareData
		if (includeForecast && data && data.length > 0) {
			const maxGroupings = 10;
			const groupings = getGroupings(data, maxGroupings, false, includeForecast);
			const preparedData = keepOnlyTopGroupings(data, groupings);
			const groupedByGrouping = _.groupBy(preparedData, "grouping");
			const tp = {};
			for (const [grouping, items] of Object.entries(groupedByGrouping)) {
				// Create a copy to avoid mutating the original array
				const sortedItems = [...items].sort((a, b) => moment(a.time).valueOf() - moment(b.time).valueOf());
				let lastActualIndex = -1;
				for (let i = sortedItems.length - 1; i >= 0; i--) {
					if (sortedItems[i].measure && sortedItems[i].measure > 0) {
						lastActualIndex = i;
						break;
					}
				}
				if (lastActualIndex >= 0) {
					tp[grouping] = sortedItems[lastActualIndex].time;
				}
			}
			transitionPointsRef.current = tp;
		} else {
			transitionPointsRef.current = null;
		}
	}, [pdata, data, includeForecast]);

	const verticalAlign = legendPosition === "right" ? "top" : "bottom";
    const align = legendPosition === "right" ? "right" : "center";
    const layout = legendPosition === "right" ? "vertical" : "horizontal";

	// Create handlers that take header as parameter - these will be called inline in the map
	const handleAreaMouseEnter = useCallback((header) => {
		// Clear any pending leave timeout first
		if (leaveTimeoutRef.current) {
			clearTimeout(leaveTimeoutRef.current);
			leaveTimeoutRef.current = null;
		}
		
		// Always update both ref and state to ensure consistency
		hoveredAreaRef.current = header;
		setHoveredArea(header);
		setLastHoveredArea(header);
	}, []);

	const handleAreaMouseLeave = useCallback((header) => {
		return (e) => {
			// Don't do anything on area mouse leave - let the chart container handle clearing
			// This prevents flickering when moving between stacked areas
			// The chart container's onMouseLeave will handle clearing when truly leaving the chart
			// If moving to another area, its onMouseEnter will update the hover state
		};
	}, []);

	const handleAreaMouseDown = useCallback((header) => {
		return (event) => {
			// Handle right mouse button clicks (button 2)
			if (event.button === 2) {
				// Check if event has preventDefault method before calling it
				if (event && typeof event.preventDefault === 'function') {
					event.preventDefault();
				}
				if (event && typeof event.stopPropagation === 'function') {
					event.stopPropagation();
				}
				// Set the hovered area to this specific area
				setHoveredArea(header);
				setLastHoveredArea(header);
				
				// Toggle menu: close if open, open if closed
				setMenuOpen(prev => {
					if (prev) {
						setIsRightClickContext(false);
						return false;
					} else {
						// Use clientX/clientY as fallback if pageX/pageY are not available
						const x = event?.pageX || event?.clientX || 0;
						const y = event?.pageY || event?.clientY || 0;
						setMenuPos({ x, y });
						setIsRightClickContext(true);
						return true;
					}
				});
			}
		};
	}, []);

	// Handle chart mouse leave to clear hover (for active dots)
	const handleChartMouseLeave = useCallback((e) => {
		// Clear any pending timeouts
		if (leaveTimeoutRef.current) {
			clearTimeout(leaveTimeoutRef.current);
			leaveTimeoutRef.current = null;
		}
		
		// Check if we're moving to another element within the chart container
		const relatedTarget = e.relatedTarget;
		
		// Check if relatedTarget is a valid DOM element (Element or Node)
		if (relatedTarget && relatedTarget.nodeType && typeof relatedTarget.closest === 'function') {
			const chartContainer = chartContainerRef.current;
			const chartWrapper = relatedTarget.closest('.recharts-wrapper');
			
			// Check if we're still within the chart container
			if (chartContainer && typeof chartContainer.contains === 'function' && chartContainer.contains(relatedTarget)) {
				// Still within chart container, don't clear
				return;
			}
			
			if (chartWrapper) {
				// Still within chart wrapper, don't clear
				return;
			}
		}
		
		// We're leaving the chart container - clear hover state
		hoveredAreaRef.current = null;
		setHoveredArea(null);
	}, []);

	// Get stroke color and style - forecast is neutral and dashed, actual is blue and solid
	const getStrokeColor = (header) => {
		const isForecast = header && header.endsWith(" forecast");
		return isForecast ? "hsl(var(--chart-pred))" : "hsl(var(--chart-1))";
	};

	const getStrokeDasharray = (header) => {
		const isForecast = header && header.endsWith(" forecast");
		return isForecast ? "5 5" : undefined; // Dashed for forecast, solid for actual
	};

	const getDotColor = (header) => {
		const isForecast = header && header.endsWith(" forecast");
		return isForecast ? "hsl(var(--chart-pred))" : "hsl(var(--chart-1))";
	};


	// Create activeDot components - need to include hoveredArea to force update
	const lines = useMemo(() => {
		return sortedHeaders.map((header, idx) => {
			// Create activeDot component for this line
			// It will show when this line is hovered (checked via ref and state)
			// Store header and idx in closure
			const activeDotComponent = (props) => {
				// Show dot when tooltip is active (props are provided)
				// Also check if this line has a valid value at this point
				if (!props || props.value === null || props.value === undefined) {
					return null;
				}
				
				// Check if we're at a transition point for forecast lines
				// At transition point, forecast dots should use actual color
				let dotColor = getDotColor(header);
				const isForecast = header && header.endsWith(" forecast");
				if (isForecast && transitionPointsRef.current && props.payload) {
					const baseGrouping = header.replace(" forecast", "");
					const transitionTime = transitionPointsRef.current[baseGrouping];
					const currentTime = props.payload._original;
					
					// If we're at the transition point, use actual color instead of forecast color
					if (transitionTime && currentTime && moment(transitionTime).isSame(moment(currentTime), 'day')) {
						dotColor = "hsl(var(--chart-1))"; // Use actual color
					}
				}
				
				// Return the dot element for this line
				return (
					<circle
						cx={props.cx}
						cy={props.cy}
						r={6}
						fill={dotColor}
						stroke="#fff"
						strokeWidth={2}
					/>
				);
			};
			
			return (
				<Line
					isAnimationActive={false}
					type="monotone"
					key={header}
					dataKey={header}
					yAxisId="main"
					stroke={getStrokeColor(header)}
					strokeWidth={2}
					strokeDasharray={getStrokeDasharray(header)}
					dot={false}
					activeDot={activeDotComponent}
					connectNulls={false}
					onMouseEnter={() => handleAreaMouseEnter(header)}
					onMouseLeave={handleAreaMouseLeave(header)}
					onClick={(data, index) => {
						// data: contains info about the line/point clicked on
						// index: the index in your data array
						// Implement logic to mark this line as selected
					  }}
					onMouseDown={handleAreaMouseDown(header)}
				/>
			);
		});
	// hoveredArea and includeForecast are listed on purpose: recomputing on hover is what
	// gives Recharts new area elements to restyle. Dropping them would stop the redraw.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sortedHeaders, handleAreaMouseEnter, handleAreaMouseLeave, handleAreaMouseDown, hoveredArea, includeForecast]);
	


	function generateNiceTicks(min, max, count) {
		// Add a small buffer (e.g., 5%) to the max value for a nicer axis
		max = max + (max - min) * 0.05;

		// Improved tick generation: handles values < 1 and avoids hard max of 3
		if (count <= 1 || min === max) {
			return [min];
		}
		const range = max - min;
		const rawStep = range / (count - 1);
		// Use a "nice" step size (rounded to nearest power of 10 fraction)
		const magnitude = Math.pow(10, Math.floor(Math.log10(Math.abs(rawStep))));
		const niceStep = Math.max(magnitude / 10, Math.round(rawStep / (magnitude / 10)) * (magnitude / 10));
		const ticks = [];
		let tick = min;
		for (let i = 0; i < count; i++) {
			ticks.push(Number(tick.toFixed(10)));
			tick += niceStep;
		}
		// Ensure last tick is exactly max
		ticks[ticks.length - 1] = max;
		return ticks;
	  }

	
	// Build tooltip data from Recharts payload
	const buildTooltipData = useCallback((props) => {
		if (!props || !props.payload || props.payload.length === 0) {
			return null;
		}

		// Get the first payload item to access the data point
		const firstPayload = props.payload[0];
		const dataPoint = firstPayload?.payload;
		
		if (!dataPoint) return null;

		// Extract date/time information - use props.label as fallback
		const label = props.label || dataPoint._name || '';
		const original = dataPoint._original || dataPoint._name || props.label || '';
		const total = dataPoint._total || 0;

		// Process data to group forecast and actual values
		const groupedPayload = {};
		
		// Get all values from the data point
		Object.entries(dataPoint).forEach(([key, val]) => {
			if (key.startsWith('_') || key.endsWith('_prev')) return;
			
			const isForecast = key.endsWith(" forecast");
			const baseName = isForecast ? key.replace(" forecast", "") : key;
			
			if (!groupedPayload[baseName]) {
				groupedPayload[baseName] = { measure: null, forecast: null };
			}
			
			if (isForecast) {
				groupedPayload[baseName].forecast = { name: key, value: val };
			} else {
				groupedPayload[baseName].measure = { name: key, value: val };
			}
		});

		// Build payload array with items to display
		const payload = [];
		Object.entries(groupedPayload).forEach(([baseName, groupItems]) => {
			let itemToShow = null;
			let displayName = baseName;
			
			if (groupItems.measure && groupItems.forecast) {
				const measureValue = groupItems.measure.value || 0;
				const forecastValue = groupItems.forecast.value || 0;
				
				if (measureValue === 0 && forecastValue > 0) {
					itemToShow = groupItems.forecast;
					displayName = baseName + " forecast";
				} else {
					itemToShow = groupItems.measure;
				}
			} else if (groupItems.measure) {
				itemToShow = groupItems.measure;
			} else if (groupItems.forecast) {
				itemToShow = groupItems.forecast;
				displayName = baseName + " forecast";
			}
			
			if (itemToShow && itemToShow.value !== undefined && itemToShow.value !== null) {
				const itemValue = itemToShow.value;
				const itemPrevValue = dataPoint[`${itemToShow.name}_prev`];
				const increase = itemPrevValue ? ((itemValue - itemPrevValue) / itemPrevValue) * 100 : 0;
				
				// Get color based on header index in sortedHeaders
				const headerIndex = sortedHeaders.indexOf(displayName);
				const isForecast = displayName.endsWith(" forecast");
				// Use red for forecast, otherwise use color from fillColors array
				const fill = isForecast ? "hsl(var(--chart-pred))" : (headerIndex >= 0 ? fillColors[headerIndex % fillColors.length] : "hsl(var(--chart-1))");
				
				payload.push({
					name: displayName,
					value: itemValue,
					fill: fill,
					payload: {
						...dataPoint,
						prevValue: itemPrevValue,
						increase: increase,
						total: total
					}
				});
			}
		});

		return {
			label: label,
			original: original,
			payload: payload
		};
	}, [sortedHeaders]);

	// Render tooltip content
	const renderTooltipContent = useCallback(({ data }) => {
		return <CustomTooltipContent tooltipData={data} dateFormat={dateFormat} sortedHeaders={sortedHeaders} forecastType={forecastType} />;
	}, [dateFormat, sortedHeaders, forecastType]);

	return (
		<div className={`${useOwnBackground ? 'bg-card mt-6 rounded-card border border-card-bd mr-6 p-4 mb-8' : 'flex flex-col h-full flex-1 overflow-hidden bg-card border border-card-bd rounded-card p-4'}`} onMouseDown={handleClickOutside}>
			{pdata && pdata.length > 0 ? (
				<div className="flex-1 min-h-0 relative">
					{useOwnBackground && (
						<div className="mb-6 cursor-default">
							<div className="text-text text-lg font-semibold mt-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
								{title || (selectedSeries ? `Forecast for ${formatSeriesName(selectedSeries)}` : "Forecast")}
							</div>
							{subtitle && (
								<div className="text-text-2 text-sm mt-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
									{subtitle}
								</div>
							)}
						</div>
					)}
					{!useOwnBackground && (
						<div className="forecast-chart-title-container absolute top-0 left-0 z-10 p-2 pointer-events-none">
							<div className="text-text text-lg font-semibold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
								{title || (selectedSeries ? `Forecast for ${formatSeriesName(selectedSeries)}` : "Forecast")}
							</div>
							{subtitle && (
								<div className="text-text-2 text-sm mt-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", paddingBottom: '20px' }}>
									{subtitle}
								</div>
							)}
						</div>
					)}
					<div className={useOwnBackground ? `flex flex-row ${showTotals ? 'gap-4' : ''}` : `absolute inset-0 ${subtitle ? 'top-[68px]' : 'top-8'} flex flex-row ${showTotals ? 'gap-4' : ''}`}>
						<div className="flex-1" style={{ minWidth: 0 }}>
						<div 
							ref={chartContainerRef}
							onContextMenu={handleRightClick}
							onMouseLeave={handleChartMouseLeave}
							style={{ position: 'relative', width: '100%', height: '100%' }}
						>
							<ResponsiveContainer width="100%" height="100%">
						<LineChart
							data={filteredPdata}
							margin={{
								top: 30,
								left: 0,
								bottom: 20,
								right: 40,
							}}
							isAnimationActive={false}
						>
						<CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--chart-grid))" />
						<XAxis dataKey="_name" tick={{ fontSize: 12, fill: "hsl(var(--chart-axis))" }} stroke="hsl(var(--chart-axis))" />
						<YAxis yAxisId="main" tick={{ fontSize: 12, fill: "hsl(var(--chart-axis))" }} stroke="hsl(var(--chart-axis))" tickFormatter={(value) => formatYAxis(value, forecastType)} domain={[0, 'dataMax']} ticks={generateNiceTicks(0, maxValue, 4)} orientation="left" />
						<YAxis yAxisId="right" tick={{ fontSize: 12, fill: "hsl(var(--chart-axis))" }} stroke="hsl(var(--chart-axis))" tickFormatter={(value) => formatYAxis(value, forecastType)} domain={[0, 'dataMax']} ticks={generateNiceTicks(0, maxValue, 4)} orientation="right" />
						<RechartsTooltip
							cursor={{ stroke: "hsl(var(--chart-axis))", strokeWidth: 1, strokeDasharray: '3 3' }}
							content={SmartRechartsTooltip.createContentCallback({
								buildTooltipData,
								renderContent: renderTooltipContent,
								estimatedWidth: 300,
								estimatedHeight: 250,
								offsetX: 50,
								offsetY: -175,
							})}
						/>

						{lines}

						{/* Legend */}
						{!showTotals && (
							<Legend
								verticalAlign={verticalAlign}
								align={align}
								layout={layout}
								iconType="circle"
								content={renderCustomLegend}
							/>
						)}
					</LineChart>
					</ResponsiveContainer>
						</div>
						</div>
						{/* Totals section */}
						{showTotals && (
							<div className="flex-shrink-0 w-96 cursor-default">
								<div className="bg-card px-4 h-full">
									{/* <div className="text-text text-sm font-semibold mb-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
										Totals
									</div> */}
									<div className="space-y-2">
                                        <div className="flex flex-col py-3">
                                            {/* Current month */}
                                            <div className="flex flex-row items-center justify-between">
                                                <div className="flex items-center flex-1 min-w-0">
                                                    <Surface width={10} height={10} className="mr-2 flex-shrink-0">
                                                        <Symbols cx={5} cy={5} type="circle" size={75} fill="hsl(var(--chart-1))" />
                                                    </Surface>
                                                    <span className="text-text-2 truncate">
														{forecastType === "utilization"
															? `Current average utilization in ${previousMonthName}`
															: `Current spent in ${previousMonthName}`}
													</span>
                                                </div>
                                            </div>
                                            <div className="flex flex-row items-center justify-between mt-1">
                                                <div className="flex flex-col items-start ml-5">
                                                    <span className="text-text-2 text-xl font-semibold">
                                                        {formatValue(previousMonthTotal, getValueFormat(forecastType), false)} 
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Forecast */}
                                            <div className="flex flex-row items-center justify-between mt-4">
                                                <div className="flex items-center flex-1 min-w-0">
                                                    <Surface width={10} height={10} className="mr-2 flex-shrink-0">
                                                        <Symbols cx={5} cy={5} type="circle" size={75} fill="hsl(var(--chart-pred))" />
                                                    </Surface>
                                                    <span className="text-text-2 truncate">
														{forecastType === "utilization"
															? `Expected average utilization in ${currentMonthName}`
															: `Expected spent in ${currentMonthName}`}
													</span>
                                                </div>
                                            </div>
                                            <div className="flex flex-row items-center justify-between mt-1">
                                                <div className="flex flex-col items-start ml-5">
                                                    <div className="text-text-2 text-xl font-semibold">
                                                        {formatValue(forecastedCurrentMonthTotal, getValueFormat(forecastType), false)} 
                                                    </div>
													<div className="text-text-2 text-sm mt-1">
														{previousMonthTotal === 0 
															? "--" 
															: (() => {
																const diff = forecastedCurrentMonthTotal - previousMonthTotal;
																const percentDiff = (diff / previousMonthTotal) * 100;
																return `${formatPercentage(percentDiff, true)} vs previous month`;
															})()
														}
													</div>
                                                </div>
                                            </div>
                                        </div>
									</div>
								</div>
							</div>
						)}
					</div>
				</div>
			) : (
				<div className="flex-1 min-h-0 relative">
					<div className="absolute inset-0 flex items-center justify-center text-text-2">
						No data available
					</div>
				</div>
			)}

			{menuOpen && (
			<DropdownMenu.Root defaultOpen={true} onOpenChange={handleMenuOpenChange}>
				<DropdownMenu.Portal>
					<DropdownMenu.Content
						className="DropdownMenuContent"
						sideOffset={5}
						style={{
						position: "absolute",
						top: menuPos.y-4,
						left: menuPos.x,
						zIndex: 9999,
						width: "auto",
						minWidth: "fit-content",
						whiteSpace: "nowrap",
						maxWidth: "none",
						}}
						onInteractOutside={(event) => {
							// Only close on left click outside, not right click
							if (event.detail.originalEvent.button === 0) {
								handleClose();
							}
						}}
					>
						<DropdownMenu.Item 
							className="DropdownMenuItem"
							style={{ paddingRight: "15px" }}
							onPointerDown={(event) => {
								// Only handle left mouse button clicks (button 0)
								if (event.button === 0) {
									event.preventDefault();
									event.stopPropagation();
									// Add filter for the selected area
									if (addFilter && (hoveredArea || lastHoveredArea) && currentGrouping) {
										const areaName = hoveredArea || lastHoveredArea;
										// Create a filter object for the area
										const filter = {
											category: currentGrouping.value,
											category_display: currentGrouping.label,
											term: areaName
										};
										addFilter(filter);
									}
									handleClose();
								}
							}}
							onPointerUp={(event) => {
								// Prevent any default behavior on pointer up
								event.preventDefault();
								event.stopPropagation();
							}}
							onContextMenu={(event) => {
								// Handle right mouse button clicks
								if (event && typeof event.preventDefault === 'function') {
									event.preventDefault();
								}
								if (event && typeof event.stopPropagation === 'function') {
									event.stopPropagation();
								}
								// Add filter for the selected area
								if (addFilter && (hoveredArea || lastHoveredArea) && currentGrouping) {
									const areaName = hoveredArea || lastHoveredArea;
									// Create a filter object for the area
									const filter = {
										category: currentGrouping.value,
										category_display: currentGrouping.label,
										term: areaName
									};
									addFilter(filter);
								}
								handleClose();
							}}
							onSelect={(event) => {
								// Prevent default selection behavior
								event.preventDefault();
							}}
						>
						Select {hoveredArea || lastHoveredArea}
						</DropdownMenu.Item>
					</DropdownMenu.Content>
				</DropdownMenu.Portal>
			</DropdownMenu.Root>
			)}
		</div>
	);
};

function formatYAxis(value, forecastType) {
	const format = getValueFormat(forecastType);
	return formatValue(value, format, false);
}

const renderCustomLegend = (props) => {
	return (
	  <ul className="list-none flex p-0 justify-center mt-2">
		<li className="flex items-center mr-4">
			{/* Colored Dot */}
			<Surface width={10} height={10} className="mr-2">
				<Symbols cx={5} cy={5} type="circle" size={75} fill="hsl(var(--chart-1))" />
			</Surface>
			{/* Series Name */}
			<span className="text-text-2 text-xs">Actual</span>
		</li>
		<li className="flex items-center mr-4">
			{/* Colored Dot */}
			<Surface width={10} height={10} className="mr-2">
				<Symbols cx={5} cy={5} type="circle" size={75} fill="hsl(var(--chart-pred))" />
			</Surface>
			{/* Series Name */}
			<span className="text-text-2 text-xs">Forecast</span>
		</li>
	  </ul>
	);
  };

// Helper function to get "since..." text based on date format
const getSinceText = (dateFormat) => {
	if (dateFormat === "MMM D") {
		return "since the day before";
	} else if (dateFormat === "YYYY-WW") {
		return "since the week before";
	} else if (dateFormat === "MMM YYYY") {
		return "since the month before";
	} else if (dateFormat === "YYYY-Q") {
		return "since the quarter before";
	} else if (dateFormat === "YYYY") {
		return "since the year before";
	}
	return "";
};

// Custom tooltip content component
const CustomTooltipContent = ({ tooltipData, dateFormat, sortedHeaders, forecastType }) => {
	if (!tooltipData || !tooltipData.payload || tooltipData.payload.length === 0) {
		return null;
	}

	const { payload, original } = tooltipData;
	const sinceText = getSinceText(dateFormat);
	const valueFormat = getValueFormat(forecastType);

	// Format date as "dddd, MMMM D" regardless of grouping or dateFormat
	const dateMoment = moment(original);
	const dateDisplay = dateMoment.format("dddd, MMMM D");

	return (
		<>
			<div className="flex flex-col mb-2 text-text-2">
				<div className="flex flex-row">
					<p className="flex-1 label mr-4 text-text-2 font-semibold">
						{dateDisplay}
					</p>
				</div>
			</div>
    
			{payload.map((item, idx) => {
				const percentageText = Math.abs(item.payload.increase) >= 1 ? formatPercentage(item.payload.increase, true) : "";
				const displayText = percentageText ? `${percentageText} ${sinceText}` : "";
				
				return (
					<div className="value flex flex-col items-start mb-2" key={idx}>
						<span
							className="flex-1 mr-4 mb-1 text-text-2 whitespace-nowrap overflow-hidden text-ellipsis max-w-full"
							style={{
								display: "inline-block",
								width: "100%",
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis"
							}}
							title={item.name}
						>
							{item.name}
						</span>

						<span className="w-[150px] text-xl font-semibold mr-3 flex items-baseline text-text-2">{formatValue(item.value, valueFormat, false)}</span>
						<span className="text-text-2">{displayText}</span>
					</div>
				);
			})}
		</>
	);
};

ForecastChartComponent.propTypes = {
	data: PropTypes.array,
	title: PropTypes.string,
	subtitle: PropTypes.string,
	selectedSeries: PropTypes.string,
	dateFormat: PropTypes.string,
	legendPosition: PropTypes.oneOf(['bottom', 'right']),
	dateGrouping: PropTypes.object,
	setDateGrouping: PropTypes.func,
	showButtons: PropTypes.bool,
	stacked: PropTypes.bool,
	filters: PropTypes.array,
	setFilters: PropTypes.func,
	addFilter: PropTypes.func,
	token: PropTypes.string,
	currentGrouping: PropTypes.object,
	height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	useOwnBackground: PropTypes.bool,
	includeForecast: PropTypes.bool,
	showTotals: PropTypes.bool,
	forecastType: PropTypes.string
};

